import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertTriangle, Activity } from 'lucide-react';

// Data Integrity Badge Component
export const DataIntegrityBadge = ({ status = 'verified' }) => {
  const config = {
    verified: {
      bg: 'bg-stable/10',
      text: 'text-stable',
      border: 'border-stable/30',
      icon: CheckCircle2,
      label: 'VERIFIED',
    },
    estimated: {
      bg: 'bg-warning/10',
      text: 'text-warning',
      border: 'border-warning/30',
      icon: AlertTriangle,
      label: 'ESTIMATED',
    },
    simulated: {
      bg: 'bg-primary/10',
      text: 'text-primary',
      border: 'border-primary/30',
      icon: Activity,
      label: 'SIMULATED',
    },
  };

  const c = config[status] || config.verified;
  const Icon = c.icon;

  return (
    <Badge className={`${c.bg} ${c.text} ${c.border} border font-medium text-xs`}>
      <Icon className="w-3 h-3 mr-1" />
      {c.label}
    </Badge>
  );
};

// Last Updated Component
export const LastUpdated = ({ date, className = '' }) => {
  const displayDate = date || new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <span className={`text-xs text-muted-foreground ${className}`}>
      Data last updated: <span className="font-medium">{displayDate}</span>
    </span>
  );
};

// Page Context Header Component
export const PageContextHeader = ({ 
  icon: Icon, 
  title, 
  description, 
  badge,
  badgeVariant = 'primary',
  lastUpdated,
  dataStatus = 'verified',
  showDataBadge = true,
  className = ''
}) => {
  const badgeColors = {
    primary: 'bg-primary/10 text-primary border-primary/30',
    stable: 'bg-stable/10 text-stable border-stable/30',
    warning: 'bg-warning/10 text-warning border-warning/30',
  };

  return (
    <div className={`mb-8 ${className}`} data-testid="page-context-header">
      {/* Top row with badge and data status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {badge && (
            <Badge className={`${badgeColors[badgeVariant]} border font-medium`}>
              {badge}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          {showDataBadge && <DataIntegrityBadge status={dataStatus} />}
          {lastUpdated !== false && <LastUpdated date={lastUpdated} />}
        </div>
      </div>

      {/* Title and description */}
      <div className="flex items-start gap-4">
        {Icon && (
          <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-6 h-6 text-primary" />
          </div>
        )}
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-2">
            {title}
          </h1>
          {description && (
            <p className="text-base text-muted-foreground leading-relaxed max-w-3xl">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Section Header Component
export const SectionHeader = ({ 
  icon: Icon, 
  title, 
  description,
  action,
  className = '' 
}) => (
  <div className={`mb-6 ${className}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
        )}
        <div>
          <h2 className="text-lg font-semibold text-foreground tracking-tight">{title}</h2>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      {action && action}
    </div>
  </div>
);

// KPI Strip Component
export const KPIStrip = ({ metrics, className = '' }) => (
  <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl bg-muted/30 border border-border/50 ${className}`}>
    {metrics.map((metric, i) => (
      <div key={i} className="text-center">
        <p className="text-2xl font-bold text-foreground">{metric.value}</p>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{metric.label}</p>
      </div>
    ))}
  </div>
);

// Platform Footer Component
export const PlatformFooter = ({ showDisclaimer = true }) => (
  <footer className="border-t border-border py-8 bg-card/50 mt-auto">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
      <div className="flex items-center justify-center gap-2 mb-3">
        <span className="font-bold text-foreground text-lg">YBV</span>
        <span className="text-muted-foreground">Brand Intelligence Platform</span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Strategic Analytics for Sports Entities | ISO 10668 Aligned
      </p>
      {showDisclaimer && (
        <p className="text-xs text-muted-foreground max-w-3xl mx-auto">
          All valuations and projections are directional indicators for strategic planning purposes only. 
          Not financial advice.
        </p>
      )}
    </div>
  </footer>
);

export default {
  PageContextHeader,
  SectionHeader,
  DataIntegrityBadge,
  LastUpdated,
  KPIStrip,
  PlatformFooter,
};
